// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import moment from 'moment-timezone';
import { Box, useTheme } from '@mui/system';
import { Typography, Divider, CircularProgress } from '@mui/material';
import { useAppSelector } from 'Hooks';
import { RootState } from 'Store';
import TextEditor from 'Components/TextEditor';
import OverageRate from './overageRate';
import stylesDef from './styles';

moment.tz.setDefault('America/New_York');

interface PricePerCreditProps {
  // The contract-rate editor's save (the parent owns the open-estimates dialog it can answer with)
  onUpdateCostPerAcre: (value: string) => void;
  loadingPricePerCredit: boolean;
}

const dollars = (value: number) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * What the account pays per credit (AUT-6569), on every account: the Credit Ladder rate its catalog orders price at,
 * with the license-fee column its setting makes apply, and the rate its AEC orders price at — each with where it
 * comes from and where it is changed. AEC stays on its plan's pricing until AEC moves onto the ladder (AUT-6571);
 * the Overage line under it is what a credit beyond a balance account's balance costs (AUT-6571 Chunk 2).
 */
export const PricePerCredit = ({ onUpdateCostPerAcre, loadingPricePerCredit }: PricePerCreditProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  const { user } = useAppSelector((state: RootState) => state.auth);
  const { org, automateSubscription } = useAppSelector((state: RootState) => state.admin);
  const ladder = org?.ladderRate;
  const licenseFeeMode = org?.licenseFee?.mode || 'none';
  const feeApplies = licenseFeeMode === 'added';
  const committedTier = org?.currentCommittedTierInfo;
  const planInfo = org?.currentPricingPlanInfo;
  const planIsPayg = planInfo?.name === 'no_commit';
  // a migrated account (AUT-6571): its plan row prices at its Credit Ladder tier, the database's Standard being Entry
  const planOnLadder = planInfo?.pricingSource === 'ladder';
  const ladderTierName = planInfo?.ladderTier === 'Standard' ? 'Entry' : planInfo?.ladderTier;
  const hasAutomateSubscription = Object.keys(automateSubscription || {}).length > 0;
  // an account with neither a plan nor a live Automate subscription prices no AEC orders, so no fee line either
  const hasAecRate = !!planInfo || hasAutomateSubscription;
  const costPerAcre = org?.costPerAcre;

  const renderLadderSource = () => {
    if (!ladder) return null;
    if (ladder.source === 'committed') {
      const setBy = committedTier?.changedBy ? ` by ${committedTier.changedBy}` : '';
      const setOn = committedTier?.startDate ? ` on ${moment(committedTier.startDate).format('LL')}` : '';
      return `Committed tier, set${setBy}${setOn}.`;
    }
    if (ladder.source === 'earned') {
      return `Earned by ${dollars(ladder.rollingSpendDollars)} of spend in the last 12 months.`;
    }
    return 'Entry, the default.';
  };

  const renderAecRate = () => {
    if (planInfo) {
      const rate = typeof planInfo.rate === 'number' ? dollars(planInfo.rate) : null;
      if (planIsPayg) {
        // the pay-as-you-go price carries the license fee inside it; the $50 + $5 split is true of the standard rate only
        const licenseNote = planInfo.rate === 55 ? ' ($50 + 10% license)' : ' (license fee included)';
        return (
          <>
            <Typography sx={styles.rateLine}>{`${rate || '—'} per credit · pay-as-you-go rate${licenseNote}`}</Typography>
            <Typography variant="body2" sx={styles.sourceText}>Change it with Edit PAYG Rate under Plan Info.</Typography>
          </>
        );
      }
      if (planOnLadder) {
        return (
          <>
            <Typography sx={styles.rateLine}>{`${rate || '—'} per credit · ${ladderTierName || 'ladder tier'} (Credit Ladder)`}</Typography>
            <Typography variant="body2" sx={styles.sourceText}>Moved onto the Credit Ladder; change the tier in the Committed Tier panel.</Typography>
          </>
        );
      }
      return (
        <>
          <Typography sx={styles.rateLine}>{`${rate || '—'} per credit · plan tier rate`}</Typography>
          <Typography variant="body2" sx={styles.sourceText}>Change it with Edit Plan under Plan Info.</Typography>
        </>
      );
    }
    if (hasAutomateSubscription) {
      return (
        <>
          <Box sx={styles.editorRow}>
            {loadingPricePerCredit && (
              <Box sx={styles.pricePerCreditProgress}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!loadingPricePerCredit && user?.pricingAccess && (
              <TextEditor initialValue={String(costPerAcre)} updateValue={(value) => onUpdateCostPerAcre(value)} formatValue={(value) => `$${value}`} />
            )}
            {!loadingPricePerCredit && !user?.pricingAccess && <Typography>{`$${costPerAcre}`}</Typography>}
            <Typography>per credit · contract rate</Typography>
          </Box>
          <Typography variant="body2" sx={styles.sourceText}>
            {user?.pricingAccess ? 'Edit the rate here; open estimates must be cleared first.' : 'Pricing access is needed to change it.'}
          </Typography>
        </>
      );
    }
    return <Typography sx={styles.muted}>No AEC rate — assign a plan.</Typography>;
  };

  return (
    <Box>
      <Typography variant="h4" sx={styles.sectionTitle}>Price per Credit</Typography>
      <Divider sx={styles.divider} />
      <Box sx={styles.block}>
        <Typography sx={styles.blockTitle}>Catalog orders — Credit Ladder</Typography>
        {!ladder && <Typography sx={styles.muted}>The Credit Ladder is not configured in this environment.</Typography>}
        {ladder && (
          <>
            <Box sx={styles.rateLine}>
              <Typography>{ladder.displayName}</Typography>
              <Typography sx={styles.muted}>·</Typography>
              <Typography sx={feeApplies ? styles.muted : styles.applies}>
                {`${dollars(ladder.dollarPerCredit)} per credit${feeApplies ? '' : ' (applies)'}`}
              </Typography>
              <Typography sx={styles.muted}>·</Typography>
              <Typography sx={feeApplies ? styles.applies : styles.muted}>
                {`${dollars(ladder.dollarPerCreditInclLicenseFee)} with the license fee${feeApplies ? ' (applies)' : ''}`}
              </Typography>
            </Box>
            <Typography variant="body2" sx={styles.sourceText}>{renderLadderSource()}</Typography>
            <Typography variant="body2" sx={styles.sourceText}>
              {user?.pricingAccess ? 'Change it in the Committed Tier panel.' : 'Set in the Committed Tier panel; pricing access is needed to change it.'}
            </Typography>
          </>
        )}
      </Box>
      <Box sx={styles.block}>
        <Typography sx={styles.blockTitle}>AEC orders</Typography>
        {renderAecRate()}
        {hasAecRate && <OverageRate />}
        {feeApplies && hasAecRate && <Typography variant="body2" sx={styles.sourceText}>+ 10% license fee on each order.</Typography>}
        <Typography variant="body2" sx={styles.caption}>
          {planOnLadder
            ? 'This account\'s AEC orders price at its Credit Ladder tier.'
            : 'AEC orders use their plan\'s pricing until AEC moves onto the Credit Ladder.'}
        </Typography>
      </Box>
    </Box>
  );
};

export default PricePerCredit;
